import { CONFIG_VARS } from "../constants/configuration";

const getDeviceAndLocationPayload = async () => {
  const payload = {
    city: "N/A",
    state: "N/A",
    pincode: "N/A",
    webType: "N/A",
    ip_address: "N/A", // This will now contain IPv4
    device_attributes: [],
    latitude: null,
    longitude: null
  };

  try {
    // Step 1: Determine device type
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const isTablet = /Tablet|iPad/i.test(navigator.userAgent);
    const isDesktop = !isMobile && !isTablet;

    // Step 2: Fetch IP and location data using ipinfo.io first
    // This provides IP address, city, state, postal code, and coordinates in one call
    // Free tier: 50,000 requests/month with commercial license
    let ipInfoData = null;
    try {
      const ipInfoResponse = await fetch("https://ipinfo.io/json");
      if (ipInfoResponse.ok) {
        ipInfoData = await ipInfoResponse.json();

        // Set IP address from ipinfo.io
        payload.ip_address = ipInfoData.ip || "N/A";

        // Parse latitude and longitude from loc field (format: "lat,long")
        if (ipInfoData.loc) {
          const [lat, long] = ipInfoData.loc.split(",").map((coord) => parseFloat(coord.trim()));
          if (!isNaN(lat) && !isNaN(long)) {
            payload.latitude = lat;
            payload.longitude = long;
          }
        }

        // Set location data from ipinfo.io as initial values
        payload.city = ipInfoData.city || "N/A";
        payload.state = ipInfoData.region || "N/A";
        payload.pincode = ipInfoData.postal || "N/A";
      } else {
        console.warn("Failed to fetch location details from ipinfo.io, response not OK.");
      }
    } catch (ipError) {
      console.warn("Error fetching location details using ipinfo.io:", ipError);
    }

    // Step 3: Attempt to fetch geolocation (most accurate method) to enhance data
    // This will override IP-based location if geolocation is available and more accurate
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      // Update coordinates from geolocation (more accurate than IP-based)
      payload.latitude = position.coords.latitude;
      payload.longitude = position.coords.longitude;

      // Fetch city and state based on latitude and longitude
      const geoResponse = await fetch(`https://geocode.xyz/${payload.latitude},${payload.longitude}?json=1`);
      if (geoResponse.ok) {
        const locationData = await geoResponse.json();

        // Check if the response indicates throttling
        if (locationData.city && locationData.city.includes("Throttled!")) {
          throw new Error("Geocode.xyz API returned an error or was throttled.");
        }

        // Override with geolocation-based data if available
        if (locationData.city) payload.city = locationData.city;
        if (locationData.state) payload.state = locationData.state;
        if (locationData.postal) payload.pincode = locationData.postal;
      } else {
        console.warn("Failed to fetch location details from geocode.xyz, response not OK.");
      }
    } catch (error) {
      console.warn("Geolocation blocked or failed:", error);
      // If geolocation fails, we already have IP-based data from ipinfo.io
    }

    // Ensure IP address is set (fallback to api.ipify.org if ipinfo.io failed)
    if (!payload.ip_address || payload.ip_address === "N/A") {
      try {
        const ipv4Response = await fetch("https://api.ipify.org?format=json");
        if (ipv4Response.ok) {
          const ipv4Data = await ipv4Response.json();
          payload.ip_address = ipv4Data.ip || "N/A";
        }
      } catch (ipError) {
        console.warn("Error fetching IPv4 address from api.ipify.org:", ipError);
      }
    }

    // Step 4: Fetch user agent details
    let userAgentData = null;
    try {
      userAgentData = await navigator.userAgentData.getHighEntropyValues([
        "architecture",
        "model",
        "platform",
        "platformVersion",
        "fullVersionList"
      ]);
    } catch (error) {
      console.warn("Error fetching user agent data:", error);
    }

    // Device-specific attributes
    const platformDetails = `${userAgentData?.platform || "Unknown"} ${userAgentData?.platformVersion || "Unknown"}`;
    const deviceType = isMobile ? "mobile" : isTablet ? "tablet" : isDesktop ? "desktop" : "Web";
    payload.webType = isMobile || isTablet ? "MWeb" : "Web";

    // Fetch brand details if on mobile/tablet
    let marketingName = "N/A";
    let retailBranding = "N/A";

    if (userAgentData?.model) {
      try {
        const brandResponse = await fetch(
          `${CONFIG_VARS.LsqUrl}/v1/lsq/device-attributes?model=${userAgentData?.model}`,
          { headers: { Accept: "*/*" } }
        );
        if (brandResponse.ok) {
          const brandData = await brandResponse.json();
          marketingName = brandData?.MarketingName || "N/A";
          retailBranding = brandData?.RetailBranding || "N/A";
        } else {
          console.warn("Brand details fetch failed, response not OK.");
        }
      } catch (error) {
        console.warn("Error fetching brand details:", error);
      }
    } else {
      console.log("No device model found; skipping brand details.");
    }

    // Step 5: Construct device attributes
    payload.device_attributes = [
      { name: "mx_CustomObject_1", value: retailBranding },
      { name: "mx_CustomObject_2", value: userAgentData?.model || "N/A" },
      { name: "mx_CustomObject_3", value: marketingName },
      { name: "mx_CustomObject_4", value: platformDetails },
      { name: "mx_CustomObject_5", value: deviceType }
    ];
  } catch (error) {
    console.error("Critical error while generating payload:", error);

    // Fallback for critical error
    payload.device_attributes = [
      { name: "mx_CustomObject_1", value: "Error retrieving details" },
      { name: "mx_CustomObject_2", value: "N/A" },
      { name: "mx_CustomObject_3", value: "N/A" },
      { name: "mx_CustomObject_4", value: "Error" },
      { name: "mx_CustomObject_5", value: "unknown" }
    ];
  }

  return payload;
};

export { getDeviceAndLocationPayload };
